/**
 * The thread's cached pages, changed only by what the server said: a send's answer or a broadcast.
 * Pure transforms over the infinite query's data, so the rules a live message follows are tested
 * without a cache.
 */

import type { InfiniteData } from '@tanstack/react-query';

import type {
  TrainWithMeModulesChatDtosResponsesConversationResponse as ConversationResponse,
  TrainWithMeModulesChatDtosResponsesMessagePageResponse as MessagePage,
  TrainWithMeModulesChatDtosResponsesMessageResponse as MessageResponse,
} from '~/shared/api/generated/model';

export type ThreadData = InfiniteData<MessagePage, number | null>;

/** Every message the cached pages hold, in the server's order (newest first). */
export function threadMessages(data: { readonly pages: readonly MessagePage[] } | undefined): MessageResponse[] {
  return (data?.pages ?? []).flatMap((page) => page.messages ?? []);
}

/**
 * The thread with `message` in it: replacing the row with the same id or `clientId` wherever it is,
 * else at the head of the newest page. A broadcast for a film that finished processing replaces the
 * Processing row in place; a new message lands at the bottom of the thread.
 */
export function withMessage(data: ThreadData | undefined, message: MessageResponse): ThreadData | undefined {
  if (data === undefined) return data;
  const same = (row: MessageResponse) =>
    (typeof message.id === 'number' && row.id === message.id) ||
    (message.clientId !== undefined && row.clientId === message.clientId);
  let replaced = false;
  const pages = data.pages.map((page) => {
    const rows = page.messages ?? [];
    if (!rows.some(same)) return page;
    replaced = true;
    return { ...page, messages: rows.map((row) => (same(row) ? message : row)) };
  });
  if (replaced) return { ...data, pages };
  const [newest, ...older] = data.pages;
  if (newest === undefined) {
    return { ...data, pages: [{ messages: [message], hasMore: false, nextCursor: null }] };
  }
  return { ...data, pages: [{ ...newest, messages: [message, ...(newest.messages ?? [])] }, ...older] };
}

/** The inbox with one conversation's unread count zeroed — the read mark's own effect. */
export function withConversationRead(
  rows: ConversationResponse[] | undefined,
  conversationId: number,
): ConversationResponse[] | undefined {
  return rows?.map((row) => (row.id === conversationId ? { ...row, unreadCount: 0 } : row));
}
